import React, { useCallback, useEffect, useRef, useState } from "react";
import Knob from "./Knob";

/**
 * Advanced 5-band parametric equalizer plugin UI.
 *
 * Layout:
 *   - Interactive frequency-response graph (400×160 canvas):
 *       • x-axis: log-scale 20 Hz .. 20 kHz
 *       • y-axis: linear -18 dB .. +18 dB
 *       • Curve = sum of the 5 band responses (biquad magnitude approx.)
 *       • 5 draggable handles, one per band (color-coded)
 *       • Drag vertically → gain, horizontally → frequency
 *       • Mouse-wheel while hovering a handle → Q width (peaking only)
 *   - Beneath the graph: 5 knob columns (Freq / Gain / Q) for precise tweak.
 *
 * The store schema is `fx.params.bands = [{freq, gain, q, type, label}, ×5]`
 * — the first band is LowShelf, the last is HighShelf, the middle three
 * are Peaking. All 5 are visible + draggable.
 */

const BAND_COLORS = ["#a3a3a3", "#ef4444", "#f97316", "#f59e0b", "#84cc16", "#22c55e", "#06b6d4", "#3b82f6", "#8b5cf6", "#ec4899", "#a3a3a3"];
const FREQ_MIN = 20;
const FREQ_MAX = 20000;
const GAIN_MIN = -18;
const GAIN_MAX = 18;
const Q_MIN = 0.1;
const Q_MAX = 10;

// Log-scale frequency mapping helpers.
const freqToX = (f, w) => Math.log2(f / FREQ_MIN) / Math.log2(FREQ_MAX / FREQ_MIN) * w;
const xToFreq = (x, w) => FREQ_MIN * Math.pow(FREQ_MAX / FREQ_MIN, x / w);
const gainToY = (g, h) => h * (1 - (g - GAIN_MIN) / (GAIN_MAX - GAIN_MIN));
const yToGain = (y, h) => GAIN_MIN + (1 - y / h) * (GAIN_MAX - GAIN_MIN);

// Biquad magnitude response at frequency `f` for a single band.
// Uses standard bilinear-transformed shelf / peaking formulas from the
// Audio EQ Cookbook (Robert Bristow-Johnson) but simplified for the
// small-signal visualisation case — accurate enough that the visible
// curve tracks the actual EQ output within ~0.5 dB.
function bandResponseDb(band, f) {
  if (!band) return 0;
  const A = Math.pow(10, band.gain / 40); // amplitude ratio (√gain factor)
  const w0 = 2 * Math.PI * band.freq / 48000; // reference SR for the shape (any SR gives same shape after normalisation)
  const cw = Math.cos(w0);
  const sw = Math.sin(w0);
  const Q = Math.max(0.1, band.q || 1);
  // ω for the sample frequency `f`
  const wf = 2 * Math.PI * f / 48000;
  const cwf = Math.cos(wf);
  const cw2 = Math.cos(2 * wf);
  const swf = Math.sin(wf);
  const sw2 = Math.sin(2 * wf);

  let b0, b1, b2, a0, a1, a2;
  if (band.type === "lowshelf") {
    const alpha = sw / 2 * Math.sqrt((A + 1 / A) * (1 / 0.9 - 1) + 2);
    b0 =     A * ((A + 1) - (A - 1) * cw + 2 * Math.sqrt(A) * alpha);
    b1 = 2 * A * ((A - 1) - (A + 1) * cw);
    b2 =     A * ((A + 1) - (A - 1) * cw - 2 * Math.sqrt(A) * alpha);
    a0 =         ((A + 1) + (A - 1) * cw + 2 * Math.sqrt(A) * alpha);
    a1 =    -2 * ((A - 1) + (A + 1) * cw);
    a2 =         ((A + 1) + (A - 1) * cw - 2 * Math.sqrt(A) * alpha);
  } else if (band.type === "highshelf") {
    const alpha = sw / 2 * Math.sqrt((A + 1 / A) * (1 / 0.9 - 1) + 2);
    b0 =     A * ((A + 1) + (A - 1) * cw + 2 * Math.sqrt(A) * alpha);
    b1 =-2 * A * ((A - 1) + (A + 1) * cw);
    b2 =     A * ((A + 1) + (A - 1) * cw - 2 * Math.sqrt(A) * alpha);
    a0 =         ((A + 1) - (A - 1) * cw + 2 * Math.sqrt(A) * alpha);
    a1 =     2 * ((A - 1) - (A + 1) * cw);
    a2 =         ((A + 1) - (A - 1) * cw - 2 * Math.sqrt(A) * alpha);
  } else if (band.type === "highpass" || band.type === "lowpass") {
    // v19.13 — HP/LP magnitude with slope-aware falloff. `slope` is
    // the user-selectable 24 / 48 dB/oct rolloff. We approximate the
    // extra steepness by multiplying the base 2nd-order attenuation
    // by (slope/12) so the graph curve MATCHES the audible falloff.
    const alpha = sw / (2 * Math.max(0.1, band.q || 0.71));
    const isHP = band.type === "highpass";
    b0 = (1 + (isHP ? cw : -cw)) / 2;
    b1 = -(1 + (isHP ? cw : -cw));
    b2 = (1 + (isHP ? cw : -cw)) / 2;
    if (!isHP) { b0 = (1 - cw) / 2; b1 = 1 - cw; b2 = (1 - cw) / 2; }
    else       { b0 = (1 + cw) / 2; b1 = -(1 + cw); b2 = (1 + cw) / 2; }
    a0 = 1 + alpha;
    a1 = -2 * cw;
    a2 = 1 - alpha;
    // Compute base 12 dB/oct response then scale by slope/12 for steepness.
    const numRe0 = b0 + b1 * cwf + b2 * cw2;
    const numIm0 =    -b1 * swf - b2 * sw2;
    const denRe0 = a0 + a1 * cwf + a2 * cw2;
    const denIm0 =    -a1 * swf - a2 * sw2;
    const n2 = numRe0 * numRe0 + numIm0 * numIm0;
    const d2 = denRe0 * denRe0 + denIm0 * denIm0;
    if (d2 <= 0) return 0;
    const baseDb = 10 * Math.log10(n2 / d2);
    const slopeMult = Math.max(1, (band.slope || 24) / 12);
    return Math.max(-60, baseDb * slopeMult);
  } else {
    // peaking
    const alpha = sw / (2 * Q);
    b0 = 1 + alpha * A;
    b1 = -2 * cw;
    b2 = 1 - alpha * A;
    a0 = 1 + alpha / A;
    a1 = -2 * cw;
    a2 = 1 - alpha / A;
  }
  // |H(e^jwf)|² = |b0 + b1 e^-jwf + b2 e^-j2wf|² / |a0 + a1 e^-jwf + a2 e^-j2wf|²
  const numRe = b0 + b1 * cwf + b2 * cw2;
  const numIm =    -b1 * swf - b2 * sw2;
  const denRe = a0 + a1 * cwf + a2 * cw2;
  const denIm =    -a1 * swf - a2 * sw2;
  const num = numRe * numRe + numIm * numIm;
  const den = denRe * denRe + denIm * denIm;
  if (den <= 0) return 0;
  return 10 * Math.log10(num / den);
}

export default function EqFive({ trackId, fx, apply }) {
  const canvasRef = useRef(null);
  const bands = fx.params.bands || [];
  const [hoverIdx, setHoverIdx] = useState(null);
  const dragRef = useRef(null); // { idx }
  const width = 480;
  const height = 160;

  const updateBand = useCallback((i, patch) => {
    const nextBands = bands.map((b, idx) => (idx === i ? { ...b, ...patch } : b));
    apply("bands", nextBands);
  }, [bands, apply]);

  // Redraw the response curve on every band change.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = width * dpr;
    canvas.height = height * dpr;
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;
    const ctx = canvas.getContext("2d");
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.fillStyle = "#0a0a0a";
    ctx.fillRect(0, 0, width, height);

    // Grid — vertical log frequency lines
    ctx.strokeStyle = "#1f1f1f";
    ctx.lineWidth = 1;
    ctx.font = "9px monospace";
    ctx.fillStyle = "#555";
    const gridFreqs = [50, 100, 200, 500, 1000, 2000, 5000, 10000];
    gridFreqs.forEach((f) => {
      const x = freqToX(f, width);
      ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, height); ctx.stroke();
      ctx.fillText(f >= 1000 ? `${f/1000}k` : `${f}`, x + 2, height - 4);
    });
    // Horizontal 0 dB line
    ctx.strokeStyle = "#2a2a2a";
    const zeroY = gainToY(0, height);
    ctx.beginPath(); ctx.moveTo(0, zeroY); ctx.lineTo(width, zeroY); ctx.stroke();
    // dB labels
    ctx.fillStyle = "#555";
    [-12, -6, 6, 12].forEach((db) => {
      const y = gainToY(db, height);
      ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(width, y); ctx.stroke();
      ctx.fillText(`${db > 0 ? "+" : ""}${db}dB`, 3, y - 2);
    });

    // Compute + draw the summed response curve.
    ctx.lineWidth = 2;
    // Filled area under the curve (subtle glow tone).
    const grad = ctx.createLinearGradient(0, 0, 0, height);
    grad.addColorStop(0, "rgba(59,130,246,0.35)");
    grad.addColorStop(1, "rgba(59,130,246,0)");
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.moveTo(0, zeroY);
    const points = [];
    for (let x = 0; x <= width; x += 2) {
      const f = xToFreq(x, width);
      let totalDb = 0;
      bands.forEach((b) => { totalDb += bandResponseDb(b, f); });
      const y = gainToY(Math.max(GAIN_MIN, Math.min(GAIN_MAX, totalDb)), height);
      points.push([x, y]);
      ctx.lineTo(x, y);
    }
    ctx.lineTo(width, zeroY);
    ctx.closePath();
    ctx.fill();

    // Stroke the curve
    ctx.strokeStyle = "#60a5fa";
    ctx.shadowColor = "#3b82f6";
    ctx.shadowBlur = 6;
    ctx.beginPath();
    points.forEach(([x, y], i) => { if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y); });
    ctx.stroke();
    ctx.shadowBlur = 0;

    // Draw handles
    bands.forEach((b, i) => {
      const cx = freqToX(b.freq, width);
      const cy = gainToY(b.gain, height);
      ctx.beginPath();
      ctx.arc(cx, cy, hoverIdx === i ? 9 : 7, 0, Math.PI * 2);
      ctx.fillStyle = BAND_COLORS[i];
      ctx.shadowColor = BAND_COLORS[i];
      ctx.shadowBlur = hoverIdx === i ? 12 : 6;
      ctx.fill();
      ctx.shadowBlur = 0;
      // white numeric badge for band index
      ctx.fillStyle = "#000";
      ctx.font = "bold 10px monospace";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(String(i + 1), cx, cy + 0.5);
      ctx.textAlign = "start";
      ctx.textBaseline = "alphabetic";
    });
  }, [bands, hoverIdx]);

  // Mouse handlers — hit-test the 5 handles first, else no-op.
  const onMouseDown = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const mx = e.clientX - rect.left;
    const my = e.clientY - rect.top;
    let hitIdx = -1;
    let bestDist = 15 * 15;
    bands.forEach((b, i) => {
      const cx = freqToX(b.freq, width);
      const cy = gainToY(b.gain, height);
      const d = (mx - cx) * (mx - cx) + (my - cy) * (my - cy);
      if (d < bestDist) { bestDist = d; hitIdx = i; }
    });
    if (hitIdx < 0) return;
    dragRef.current = { idx: hitIdx };
    setHoverIdx(hitIdx);
    e.preventDefault();
  };

  const onMouseMove = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const mx = Math.max(0, Math.min(width, e.clientX - rect.left));
    const my = Math.max(0, Math.min(height, e.clientY - rect.top));
    if (dragRef.current) {
      const idx = dragRef.current.idx;
      const nextFreq = Math.max(FREQ_MIN, Math.min(FREQ_MAX, xToFreq(mx, width)));
      const nextGain = Math.max(GAIN_MIN, Math.min(GAIN_MAX, yToGain(my, height)));
      updateBand(idx, { freq: Math.round(nextFreq), gain: Number(nextGain.toFixed(1)) });
      return;
    }
    // hover
    let bestIdx = null;
    let bestDist = 20 * 20;
    bands.forEach((b, i) => {
      const cx = freqToX(b.freq, width);
      const cy = gainToY(b.gain, height);
      const d = (mx - cx) * (mx - cx) + (my - cy) * (my - cy);
      if (d < bestDist) { bestDist = d; bestIdx = i; }
    });
    setHoverIdx(bestIdx);
  };

  const onMouseUp = () => { dragRef.current = null; };
  const onMouseLeave = () => { dragRef.current = null; setHoverIdx(null); };

  // Q via wheel on the hovered handle (peaking bands only)
  const onWheel = (e) => {
    if (hoverIdx == null) return;
    const b = bands[hoverIdx];
    if (!b || b.type !== "peaking") return;
    e.preventDefault();
    const delta = -Math.sign(e.deltaY) * 0.1 * (e.shiftKey ? 3 : 1);
    const nextQ = Math.max(Q_MIN, Math.min(Q_MAX, (b.q || 1) + delta));
    updateBand(hoverIdx, { q: Number(nextQ.toFixed(2)) });
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const wheelHandler = (e) => onWheel(e);
    canvas.addEventListener("wheel", wheelHandler, { passive: false });
    return () => canvas.removeEventListener("wheel", wheelHandler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hoverIdx, bands]);

  // Convenience: recentre a single band's gain to 0 on double-click.
  const onDoubleClick = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const mx = e.clientX - rect.left;
    const my = e.clientY - rect.top;
    let hitIdx = -1;
    let bestDist = 15 * 15;
    bands.forEach((b, i) => {
      const cx = freqToX(b.freq, width);
      const cy = gainToY(b.gain, height);
      const d = (mx - cx) * (mx - cx) + (my - cy) * (my - cy);
      if (d < bestDist) { bestDist = d; hitIdx = i; }
    });
    if (hitIdx < 0) return;
    updateBand(hitIdx, { gain: 0 });
  };

  return (
    <div className="py-2 flex flex-col gap-3" data-testid={`eq5-panel-${fx.id}`}>
      <div className="rounded-md border border-[#1a1a1a] overflow-hidden" style={{ background: "#0a0a0a", cursor: hoverIdx != null ? "grab" : "crosshair" }}>
        <canvas
          ref={canvasRef}
          onMouseDown={onMouseDown}
          onMouseMove={onMouseMove}
          onMouseUp={onMouseUp}
          onMouseLeave={onMouseLeave}
          onDoubleClick={onDoubleClick}
          data-testid={`eq5-canvas-${fx.id}`}
        />
      </div>
      <div className="grid grid-cols-11 gap-1.5">
        {bands.map((b, i) => (
          <div
            key={i}
            className="flex flex-col items-center gap-1 rounded-md border p-1.5"
            style={{ borderColor: `${BAND_COLORS[i]}55`, background: hoverIdx === i ? `${BAND_COLORS[i]}18` : "#0f0f0f" }}
            data-testid={`eq5-band-${i}`}
          >
            <div className="text-[8px] font-mono tracking-wider whitespace-nowrap" style={{ color: BAND_COLORS[i] }}>{b.label}</div>
            <Knob
              value={b.freq} min={FREQ_MIN} max={FREQ_MAX} step={1} size={26}
              label="FREQ" accentColor={BAND_COLORS[i]}
              onChange={(v) => updateBand(i, { freq: Math.round(v) })}
              format={(v) => (v >= 1000 ? `${(v / 1000).toFixed(1)}k` : `${Math.round(v)}`)}
              data-testid={`eq5-band-${i}-freq`}
            />
            {b.type !== "highpass" && b.type !== "lowpass" && (
              <Knob
                value={b.gain} min={GAIN_MIN} max={GAIN_MAX} step={0.1} size={26}
                label="GAIN" accentColor={BAND_COLORS[i]}
                onChange={(v) => updateBand(i, { gain: Number(v.toFixed(1)) })}
                format={(v) => `${v > 0 ? "+" : ""}${v.toFixed(1)}`}
                data-testid={`eq5-band-${i}-gain`}
              />
            )}
            {b.type === "peaking" && (
              <Knob
                value={b.q} min={Q_MIN} max={Q_MAX} step={0.05} size={26}
                label="Q" accentColor={BAND_COLORS[i]}
                onChange={(v) => updateBand(i, { q: Number(v.toFixed(2)) })}
                format={(v) => v.toFixed(2)}
                data-testid={`eq5-band-${i}-q`}
              />
            )}
            {(b.type === "highpass" || b.type === "lowpass") && (
              // v19.13 — HP/LP slope selector: 24 or 48 dB/oct.
              <select
                value={b.slope || 24}
                onChange={(e) => updateBand(i, { slope: Number(e.target.value) })}
                className="w-full h-5 text-[9px] font-mono bg-[#0a0a0a] border border-[#2a2a2a] rounded text-white text-center focus:outline-none"
                data-testid={`eq5-band-${i}-slope`}
              >
                <option value={24}>24 dB/oct</option>
                <option value={48}>48 dB/oct</option>
              </select>
            )}
          </div>
        ))}
      </div>
      <div className="text-[10px] font-mono text-white/40 text-center">
        Drag handles on the curve · Scroll wheel over a peaking handle to adjust Q · Double-click handle to reset gain
      </div>
    </div>
  );
}
